/**
 * Kameraservice med Capacitor Camera API for native (Android/iOS)
 * og HTML file input som fallback for web.
 *
 * On Android: uses custom ExifImagePicker plugin (ACTION_OPEN_DOCUMENT)
 * to bypass Photo Picker which strips EXIF GPS data.
 */
import { Capacitor, registerPlugin } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';

// -- Types --

export interface Coordinates {
  latitude: number;
  longitude: number;
}

export interface PickPhotoResult {
  file: File;
  coords: Coordinates | null;
}

// -- Native ExifImagePicker plugin (Android only) --

interface ExifImagePickerResult {
  cancelled: boolean;
  hasGps: boolean;
  latitude: number;
  longitude: number;
  webPath: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
}

interface ExifImagePickerPlugin {
  pickImage(): Promise<ExifImagePickerResult>;
}

const ExifImagePicker = registerPlugin<ExifImagePickerPlugin>('ExifImagePicker');

// -- Public API --

/**
 * Pick a photo from gallery (or camera). Returns File + GPS coordinates if available.
 * On Android: uses custom native plugin that preserves EXIF GPS.
 * On iOS/web: uses Capacitor Camera API / file input.
 */
export async function pickPhoto(): Promise<PickPhotoResult | null> {
  if (Capacitor.getPlatform() === 'android') {
    return pickPhotoAndroid();
  }
  if (Capacitor.isNativePlatform()) {
    return pickPhotoNative();
  }
  return pickPhotoWeb();
}

/**
 * Pick photo on Android using custom ExifImagePicker plugin.
 * Uses ACTION_OPEN_DOCUMENT which bypasses Photo Picker and preserves EXIF GPS.
 */
async function pickPhotoAndroid(): Promise<PickPhotoResult | null> {
  // Step 1: Call native plugin
  let result: ExifImagePickerResult;
  try {
    result = await ExifImagePicker.pickImage();
  } catch (e: any) {
    if (e?.message?.includes('cancel') || e?.message?.includes('User cancelled')) {
      return null;
    }
    console.warn('ExifImagePicker plugin call failed:', e);
    throw e; // Propagate so caller can show feedback
  }

  if (result.cancelled || !result.webPath) return null;

  // Step 2: User selected a file — process it (errors here should NOT be silent)
  const coords = result.hasGps
    ? { latitude: result.latitude, longitude: result.longitude }
    : null;

  const webUrl = Capacitor.convertFileSrc(result.webPath);
  const response = await fetch(webUrl);
  const blob = await response.blob();
  const file = new File([blob], result.fileName, { type: result.mimeType || 'image/jpeg' });

  return { file, coords };
}

/**
 * Pick photo using Capacitor Camera API (iOS).
 * Uses CameraSource.Prompt to let user choose camera or gallery.
 */
async function pickPhotoNative(): Promise<PickPhotoResult | null> {
  try {
    const image = await Camera.getPhoto({
      resultType: CameraResultType.Uri,
      source: CameraSource.Prompt,
      quality: 95,
    });

    if (!image.path && !image.webPath) return null;

    const webPath = image.webPath || Capacitor.convertFileSrc(image.path!);
    const response = await fetch(webPath);
    const blob = await response.blob();
    const filename = `photo_${Date.now()}.${image.format || 'jpeg'}`;
    const file = new File([blob], filename, { type: `image/${image.format || 'jpeg'}` });

    // Try to extract GPS from Capacitor's exif object if present (useful if compression stripped it from the blob)
    let coords: Coordinates | null = null;
    if (image.exif) {
      try {
        const gps = image.exif['{GPS}'] || image.exif.GPS || image.exif;
        let lat = gps.Latitude ?? gps.GPSLatitude;
        let lon = gps.Longitude ?? gps.GPSLongitude;
        let latRef = gps.LatitudeRef ?? gps.GPSLatitudeRef;
        let lonRef = gps.LongitudeRef ?? gps.GPSLongitudeRef;

        if (lat != null && lon != null) {
          let latNum = typeof lat === 'number' ? lat : parseFloat(lat as string);
          let lonNum = typeof lon === 'number' ? lon : parseFloat(lon as string);
          
          if (!isNaN(latNum) && !isNaN(lonNum)) {
            if (latRef === 'S') latNum = -Math.abs(latNum);
            if (lonRef === 'W') lonNum = -Math.abs(lonNum);
            coords = { latitude: latNum, longitude: lonNum };
            console.log('[NativeCamera] Found GPS in Capacitor EXIF:', coords);
          }
        }
      } catch (e) {
        console.warn('[NativeCamera] Error parsing Capacitor EXIF:', e);
      }
    }

    return { file, coords };
  } catch (e: any) {
    if (e?.message?.includes('cancel') || e?.message?.includes('User cancelled')) {
      return null;
    }
    console.warn('Native camera pick failed:', e);
    return pickPhotoWeb();
  }
}

/** Pick photo using standard HTML file input (web fallback). */
function pickPhotoWeb(): Promise<PickPhotoResult | null> {
  return new Promise((resolve) => {
    let resolved = false;
    const done = (file: File | null) => {
      if (resolved) return;
      resolved = true;
      window.removeEventListener('focus', handleFocus);
      resolve(file ? { file, coords: null } : null);
    };

    const input = document.createElement('input');
    input.type = 'file';
    // No accept attribute — avoids Android stripping EXIF GPS metadata

    input.onchange = () => {
      done(input.files?.[0] ?? null);
    };

    // Handle cancellation via focus event (increased timeout for slower desktops)
    const handleFocus = () => {
      setTimeout(() => {
        if (!input.files?.length) done(null);
      }, 1500);
    };
    window.addEventListener('focus', handleFocus);

    input.click();
  });
}

/**
 * Legacy export for backward compatibility.
 * @deprecated Use pickPhoto() instead.
 */
export const capturePhoto = pickPhoto;
